import React, { useMemo, useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';

const CrewsView = () => {
  const {
    user,
    users,
    crews,
    crewMemberships,
    invitations,
    activeCrewId,
    setActiveCrew,
    createCrew,
    getMyCrews,
    getCrewMembers,
    inviteUserToCrew,
    inviteUserByEmailOrUsername,
    updateCrewMemberRole,
    removeCrewMember,
    leaveCrew,
    decideInvitation
  } = useAuth();

  const myCrews = useMemo(() => getMyCrews(), [crews, crewMemberships, user]);
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') return document.body.classList.contains('light') ? 'light' : 'dark';
    return 'dark';
  });
  const [newCrew, setNewCrew] = useState({ name: '', description: '' });
  const [manualInvite, setManualInvite] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [crewMembers, setCrewMembers] = useState([]);

  const activeCrew = myCrews.find(c => c.id === activeCrewId) || myCrews[0] || null;

  // Charger les membres du crew actif
  useEffect(() => {
    const loadCrewMembers = async () => {
      if (activeCrew) {
        try {
          const members = await getCrewMembers(activeCrew.id);
          setCrewMembers(members);
        } catch (e) {
          console.error('Failed to load crew members:', e);
          setCrewMembers([]);
        }
      } else {
        setCrewMembers([]);
      }
    };
    loadCrewMembers();
  }, [activeCrew, getCrewMembers]);

  // Debug: Log the crews data
  console.log('🎨 CrewsView - crews:', crews);
  console.log('🎨 CrewsView - myCrews:', myCrews);
  console.log('🎨 CrewsView - activeCrewId:', activeCrewId);
  console.log('🎨 CrewsView - activeCrew:', activeCrew);


  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Crews</h2>
        <div className="flex items-center gap-2">
          <Select value={activeCrewId || (activeCrew ? activeCrew.id : '')} onValueChange={setActiveCrew}>
            <SelectTrigger className="w-60">
              <SelectValue placeholder="Sélectionner un crew" />
            </SelectTrigger>
            <SelectContent>
              {myCrews.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="border-2">
        <CardHeader className="bg-white text-black border-b-2">
          <CardTitle>Create a crew</CardTitle>
        </CardHeader>
        <CardContent className="flex gap-2 bg-white text-black">
          <Input placeholder="Crew name" value={newCrew.name} onChange={e => setNewCrew({ ...newCrew, name: e.target.value })} className="bg-white text-black border-black" />
          <Input placeholder="Description" value={newCrew.description} onChange={e => setNewCrew({ ...newCrew, description: e.target.value })} className="bg-white text-black border-black" />
          <Button className="bg-white text-black border-2 border-black hover:bg-gray-100" onClick={async () => { 
            if (newCrew.name.trim()) { 
              await createCrew(newCrew); 
              setNewCrew({ name: '', description: '' }); 
            } 
          }}>Create</Button>
        </CardContent>
      </Card>

      {/* Invitations - affichées même sans crew actif */}
      <Card className="border-2 bg-white text-black">
        <CardHeader className="bg-white text-black border-b-2">
          <CardTitle>Invitations</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 bg-white text-black">
          {(invitations || []).length === 0 && (
            <div className="text-sm text-gray-500">No pending invitations.</div>
          )}
          {(invitations || []).map(inv => (
            <div key={inv.id} className="border rounded p-2">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold">{inv.crewName}</div>
                  <div className="text-xs text-gray-500">{inv.crewDescription}</div>
                  <div className="text-xs">Role: {inv.role}</div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={async () => { await decideInvitation({ invitationId: inv.id, accept: true }); }} className="bg-green-600 hover:bg-green-700 text-white">Accept</Button>
                  <Button onClick={async () => { await decideInvitation({ invitationId: inv.id, accept: false }); }} variant="destructive">Decline</Button>
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {activeCrew && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="border-2 bg-white text-black">
            <CardHeader className="bg-white text-black border-b-2">
              <CardTitle>Members of {activeCrew.name}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 bg-white text-black">
              {(crewMembers || []).map(m => (
                <div key={m.userId} className="flex items-center justify-between border rounded p-2">
                  <div className="flex items-center gap-2">
                    <Badge>{
                      m.user?.profile?.displayName
                      || m.user?.username
                      || m.user?.email
                      || (m.userId === user?.id
                        ? (user?.profile?.displayName || user?.username || user?.email)
                        : m.userId)
                    }</Badge>
                    <span className="text-sm">{m.user?.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={m.role} onValueChange={role => updateCrewMemberRole({ crewId: activeCrew.id, userId: m.userId, role })}>
                      <SelectTrigger className="w-28">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="owner">owner</SelectItem>
                        <SelectItem value="admin">admin</SelectItem>
                        <SelectItem value="member">member</SelectItem>
                      </SelectContent>
                    </Select>
                    {m.role !== 'owner' && (
                      <>
                        {m.userId === user?.id ? (
                          <Button 
                            variant="destructive" 
                            onClick={() => {
                              const result = leaveCrew({ crewId: activeCrew.id });
                              if (result.success) {
                                setInviteMessage(result.message);
                                setTimeout(() => setInviteMessage(''), 3000);
                              } else {
                                setInviteMessage(result.message);
                                setTimeout(() => setInviteMessage(''), 5000);
                              }
                            }}
                          >
                            Leave Crew
                          </Button>
                        ) : (
                          <Button variant="destructive" onClick={() => removeCrewMember({ crewId: activeCrew.id, userId: m.userId })}>Remove</Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}

              <div className="space-y-3 pt-2">
                {/* Invitation interface */}
                <div className="flex items-center gap-2">
                  <Input
                    value={manualInvite}
                    onChange={(e) => setManualInvite(e.target.value)}
                    placeholder="Enter email or username to invite"
                    className="w-60"
                  />
                  <Button 
                    onClick={async () => { 
                      if (manualInvite.trim()) { 
                        const result = await inviteUserByEmailOrUsername({ 
                          crewId: activeCrew.id, 
                          emailOrUsername: manualInvite.trim() 
                        });
                        setInviteMessage(result.message);
                        if (result.success) {
                          setManualInvite(''); 
                          // Effacer le message après 3 secondes
                          setTimeout(() => setInviteMessage(''), 3000);
                        }
                      } 
                    }}
                    disabled={!manualInvite.trim()}
                  >
                    Send Invite
                  </Button>
                </div>
                
                {/* Message de résultat */}
                {inviteMessage && (
                  <div className={`p-3 rounded border-2 ${
                    inviteMessage.includes('added') || inviteMessage.includes('sent') 
                      ? 'bg-green-50 border-green-300 text-green-800' 
                      : 'bg-red-50 border-red-300 text-red-800'
                  }`}>
                    {inviteMessage}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 bg-white text-black">
            <CardHeader className="bg-white text-black border-b-2">
              <CardTitle>My crews</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 bg-white text-black">
              {myCrews.map(c => (
                <div
                  key={c.id}
                  onClick={() => setActiveCrew(c.id)}
                  className={`flex items-center justify-between border rounded p-2 cursor-pointer hover:bg-gray-50 ${activeCrew?.id === c.id ? 'bg-gray-50 dark:bg-gray-900' : ''}`}
                >
                  <div>
                    <div className="font-semibold">{c.name}</div>
                    <div className="text-xs text-gray-500">{c.description}</div>
                  </div>
                  {activeCrew?.id === c.id && <Badge className="bg-black text-white">Active</Badge>}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CrewsView;

